/**
 * API Helpers for AngularJS Module Integration
 * 
 * Issue #69 - Legacy AngularJS Integration Framework
 * 
 * Provides centralized API utilities for AngularJS modules to communicate
 * with TECDocChart Django backend. Ensures HIPAA compliance, authentication
 * handling, and healthcare-specific error management.
 * 
 * Features:
 * - Django REST API integration with CSRF protection
 * - Healthcare-specific error handling
 * - PHI data protection and audit logging
 * - Multi-tenant request routing
 * - Session-based authentication
 * - Medical coding standards compliance
 */

// Global interfaces for healthcare data
export interface EmployeeContext {
  id: string;
  name: string;
  role: 'physician' | 'nurse' | 'admin' | 'tech' | 'other';
  permissions: string[];
  tenantId: string;
}

export interface TenantContext {
  id: string;
  name: string;
  domain: string;
  settings: Record<string, any>;
}

export interface AuditLogEntry {
  action: string;
  resource: string;
  resourceId?: string;
  employeeId: string;
  tenantId: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  audit?: AuditLogEntry;
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  headers?: Record<string, string>;
  auditAction?: string;
  requiresPermission?: string[];
  timeout?: number;
  retryAttempts?: number;
}

/**
 * API Helper class for AngularJS modules
 */
export class AngularApiHelper {
  private baseUrl: string;
  private employeeContext?: EmployeeContext;
  private tenantContext?: TenantContext;
  private csrfToken?: string;

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
    this.loadCsrfToken();
    this.loadContextFromGlobals();
  }

  /**
   * Load CSRF token from Django
   */
  private loadCsrfToken(): void {
    // Try to get CSRF token from various sources
    const csrfCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrftoken='))
      ?.split('=')[1];
    
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    const csrfInput = (document.querySelector('input[name="csrfmiddlewaretoken"]') as HTMLInputElement)?.value;
    
    this.csrfToken = csrfCookie || csrfMeta || csrfInput;
    
    if (!this.csrfToken) {
      console.warn('CSRF token not found. API requests may fail.');
    }
  }

  /**
   * Load healthcare context from global variables set by Django
   */
  private loadContextFromGlobals(): void {
    // Load from window globals set by Django template
    const globals = (window as any);
    
    if (globals.employeeContext) {
      this.employeeContext = globals.employeeContext;
    }
    
    if (globals.tenantContext) {
      this.tenantContext = globals.tenantContext;
    }
  }

  /**
   * Update healthcare context (called by SharedStateProvider)
   */
  public updateContext(employee?: EmployeeContext, tenant?: TenantContext): void {
    this.employeeContext = employee;
    this.tenantContext = tenant;
  }

  /**
   * Check if user has required permissions
   */
  private checkPermissions(requiredPermissions: string[]): boolean {
    if (!this.employeeContext) return false;
    if (requiredPermissions.length === 0) return true;
    
    return requiredPermissions.every(permission => 
      this.employeeContext!.permissions.includes(permission)
    );
  }

  /**
   * Create audit log entry
   */
  private createAuditLog(action: string, resource: string, resourceId?: string, metadata?: any): AuditLogEntry {
    return {
      action,
      resource,
      resourceId,
      employeeId: this.employeeContext?.id || 'unknown',
      tenantId: this.tenantContext?.id || 'unknown',
      timestamp: new Date().toISOString(),
      ipAddress: (window as any).clientIP || 'unknown',
      userAgent: navigator.userAgent,
      metadata
    };
  }

  /**
   * Make authenticated API request
   */
  public async request<T = any>(
    endpoint: string, 
    options: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      data,
      headers = {},
      auditAction,
      requiresPermission = [],
      timeout = 30000,
      retryAttempts = 0
    } = options;

    try {
      // Check permissions
      if (requiresPermission.length > 0 && !this.checkPermissions(requiresPermission)) {
        return {
          success: false,
          error: {
            code: 'PERMISSION_DENIED',
            message: `Insufficient permissions. Required: ${requiresPermission.join(', ')}`
          }
        };
      }

      // Prepare request headers
      const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...headers
      };

      // Add CSRF token for non-GET requests
      if (method !== 'GET' && this.csrfToken) {
        requestHeaders['X-CSRFToken'] = this.csrfToken;
      }

      // Add tenant header for multi-tenant routing
      if (this.tenantContext?.domain) {
        requestHeaders['X-Tenant-Domain'] = this.tenantContext.domain;
      }

      // Prepare request configuration
      const requestConfig: RequestInit = {
        method,
        headers: requestHeaders,
        credentials: 'same-origin', // Include session cookies
        signal: AbortSignal.timeout(timeout)
      };

      // Add body for non-GET requests
      if (method !== 'GET' && data) {
        requestConfig.body = JSON.stringify(data);
      }

      // Make the request
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, requestConfig);

      // Handle response
      let responseData: any;
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      // Create audit log if requested
      let audit: AuditLogEntry | undefined;
      if (auditAction && this.employeeContext) {
        audit = this.createAuditLog(auditAction, endpoint, data?.id, {
          method,
          responseStatus: response.status,
          success: response.ok
        });
        
        console.log('Healthcare API Audit:', audit);
        // In real implementation, send to audit service
        // this.sendAuditLog(audit);
      }

      if (!response.ok) {
        // Handle different types of API errors
        let errorMessage = 'API request failed';
        let errorCode = 'API_ERROR';
        
        if (response.status === 401) {
          errorCode = 'AUTHENTICATION_REQUIRED';
          errorMessage = 'Authentication required. Please log in.';
        } else if (response.status === 403) {
          errorCode = 'PERMISSION_DENIED';
          errorMessage = 'Access denied. Insufficient permissions.';
        } else if (response.status === 404) {
          errorCode = 'NOT_FOUND';
          errorMessage = 'Requested resource not found.';
        } else if (response.status >= 500) {
          errorCode = 'SERVER_ERROR';
          errorMessage = 'Server error. Please try again or contact support.';
        }

        // Try to extract error message from response
        if (responseData && typeof responseData === 'object') {
          if (responseData.error) {
            errorMessage = responseData.error;
          } else if (responseData.detail) {
            errorMessage = responseData.detail;
          } else if (responseData.message) {
            errorMessage = responseData.message;
          }
        }

        return {
          success: false,
          error: {
            code: errorCode,
            message: errorMessage,
            details: responseData
          },
          audit
        };
      }

      return {
        success: true,
        data: responseData,
        audit
      };

    } catch (error) {
      console.error('API request error:', error);
      
      let errorMessage = 'Network error or timeout';
      let errorCode = 'NETWORK_ERROR';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        if (error.name === 'AbortError') {
          errorCode = 'TIMEOUT';
          errorMessage = 'Request timeout. Please try again.';
        }
      }

      // Retry logic
      if (retryAttempts > 0 && errorCode === 'NETWORK_ERROR') {
        console.log(`Retrying API request. Attempts remaining: ${retryAttempts}`);
        return this.request(endpoint, { ...options, retryAttempts: retryAttempts - 1 });
      }

      return {
        success: false,
        error: {
          code: errorCode,
          message: errorMessage,
          details: error
        }
      };
    }
  }

  /**
   * Convenience methods for common HTTP methods
   */
  public async get<T = any>(endpoint: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public async post<T = any>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'data'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'POST', data });
  }

  public async put<T = any>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'data'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', data });
  }

  public async patch<T = any>(endpoint: string, data?: any, options: Omit<ApiRequestOptions, 'method' | 'data'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', data });
  }

  public async delete<T = any>(endpoint: string, options: Omit<ApiRequestOptions, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

/**
 * Global API helper instance for AngularJS modules
 */
export const angularApiHelper = new AngularApiHelper();

/**
 * Configure global API helper for use in AngularJS
 */
export function configureAngularApi(employee?: EmployeeContext, tenant?: TenantContext): void {
  angularApiHelper.updateContext(employee, tenant);
}

/**
 * AngularJS service factory function
 * Use this in AngularJS modules to create the API service
 */
export function createAngularApiService() {
  return function(_$http: any, $q: any) {
    return {
      // Wrap the API helper for AngularJS promises
      request: (endpoint: string, options: ApiRequestOptions = {}) => {
        const deferred = $q.defer();
        
        angularApiHelper.request(endpoint, options)
          .then(response => {
            if (response.success) {
              deferred.resolve(response.data);
            } else {
              deferred.reject(response.error);
            }
          })
          .catch(error => {
            deferred.reject(error);
          });
        
        return deferred.promise;
      },
      
      // Convenience methods
      get: (endpoint: string, options = {}) => {
        return angularApiHelper.get(endpoint, options).then(response => {
          if (response.success) return response.data;
          throw response.error;
        });
      },
      
      post: (endpoint: string, data?: any, options = {}) => {
        return angularApiHelper.post(endpoint, data, options).then(response => {
          if (response.success) return response.data;
          throw response.error;
        });
      },
      
      put: (endpoint: string, data?: any, options = {}) => {
        return angularApiHelper.put(endpoint, data, options).then(response => {
          if (response.success) return response.data;
          throw response.error;
        });
      },
      
      patch: (endpoint: string, data?: any, options = {}) => {
        return angularApiHelper.patch(endpoint, data, options).then(response => {
          if (response.success) return response.data;
          throw response.error;
        });
      },
      
      delete: (endpoint: string, options = {}) => {
        return angularApiHelper.delete(endpoint, options).then(response => {
          if (response.success) return response.data;
          throw response.error;
        });
      }
    };
  };
}

export default angularApiHelper;